/*
 * Treatment pages — English version, translated from the French reference.
 * Written for the patient: no promise of results, no prices, nothing
 * that cannot be verified. URLs are defined in i18n/config.js.
 */

const soinsEn = {
    sopk: {
        nav: "PCOS",
        title: "PCOS: medical consultation in Asnières-sur-Seine",
        description:
            "Polycystic ovary syndrome: hormonal work-up, acne, hirsutism and hair loss. Consultation with Dr Souhali Massilia in Asnières-sur-Seine, near Paris.",
        eyebrow: "Hormonal disorders",
        h1: "PCOS: understand first, then treat what shows",
        lead: "Polycystic ovary syndrome affects around one woman in ten. At the Asnières-sur-Seine clinic, the consultation first looks for the cause of the imbalance, then treats its visible effects: acne, excess hair growth, hair loss.",
        photo: { file: "bureau.jpg", alt: "The consulting room of the clinic in Asnières-sur-Seine" },
        condition: "Polycystic ovary syndrome",
        sections: [
            {
                h2: "Recognising PCOS",
                paragraphes: [
                    "PCOS is a hormonal imbalance rather than a disease of the ovaries in the strict sense. To very variable degrees it combines excess androgen production, irregular ovulation and sometimes insulin resistance. It is that combination which explains signs that appear to have nothing to do with one another.",
                    "Many women live with PCOS for years without knowing: their acne was treated, then their excess hair growth, then their hair loss, each one separately, without the signs ever being connected.",
                ],
                puces: [
                    "irregular, infrequent or absent cycles",
                    "acne that persists into adulthood, especially on the lower face",
                    "excess hair on the face, neck, chest or abdomen (hirsutism)",
                    "hair thinning on the top of the scalp",
                    "unexplained weight gain, tiredness after meals",
                    "difficulty conceiving",
                ],
            },
            {
                h2: "The work-up at the clinic",
                paragraphes: [
                    "The first consultation starts with a detailed interview: the history of your cycles, family history, treatments already tried, and the impact on daily life. The clinical examination assesses the skin, the distribution of hair growth and hair density.",
                    "A hormonal blood work-up is then prescribed, completed if needed by a metabolic work-up and a pelvic ultrasound. The aim is not to pile up tests, but to confirm the diagnosis and rule out the other possible causes of excess androgens.",
                    "The results are explained at a review consultation: what each value means, what is involved in your case, and what can be improved.",
                ],
            },
            {
                h2: "The available options",
                paragraphes: [
                    "Care is always built with you, according to what bothers you most and your life plans — including any plan for pregnancy, which changes the therapeutic options.",
                    "Depending on the situation it combines medical treatment of the hormonal imbalance, lifestyle advice targeted at insulin resistance, and treatment of the visible signs: laser hair removal or electrolysis for hirsutism, a protocol for acne, scalp mesotherapy and long-term treatment for hair loss.",
                    "Follow-up is regular: a hormonal imbalance is not corrected in a single session, and treatments are reassessed over the months.",
                ],
            },
        ],
        faq: [
            [
                "Can PCOS be cured?",
                "You do not “cure” PCOS the way you cure an infection: it is a hormonal background that changes with age and lifestyle. Its manifestations, however — acne, excess hair growth, irregular cycles, hair loss — can very often be well controlled when they are managed coherently.",
            ],
            [
                "Do I have to stop the pill to have the work-up?",
                "Not necessarily, and certainly not on your own initiative. Some hormone levels cannot be interpreted on combined contraception: the doctor will tell you, in your situation, whether the timing of the work-up needs to be adjusted.",
            ],
            [
                "Can I consult if I already have a diagnosis?",
                "Yes, and it is common. Many patients come with a known diagnosis but skin or hair signs that have never been treated, or a treatment they tolerate poorly.",
            ],
        ],
        related: ["chute-de-cheveux", "epilation-laser", "menopause"],
    },

    menopause: {
        nav: "Menopause",
        title: "Menopause and perimenopause: care in Asnières-sur-Seine",
        description:
            "Perimenopause and menopause: thinner skin, loss of firmness, vaginal dryness, hair loss. Medical consultation in Asnières-sur-Seine, near Paris.",
        eyebrow: "Women's health",
        h1: "Perimenopause and menopause: follow-up, not fate",
        lead: "Falling oestrogen levels do not only affect your cycles: they show on the skin, the hair, the figure and intimate comfort. At the Asnières-sur-Seine clinic, these changes are taken seriously and followed as a whole.",
        photo: { file: "cafe.jpg", alt: "The clinic waiting area, with its coffee corner" },
        condition: "Menopause",
        sections: [
            {
                h2: "What changes, and why",
                paragraphes: [
                    "Perimenopause often begins several years before periods stop for good. Cycles become erratic, sleep is broken, mood varies — and in parallel the skin changes. Collagen production falls markedly in the first years after the menopause: the skin thins, dehydrates and loses firmness.",
                    "The balance between oestrogens and androgens also shifts, which explains why some women see thicker hairs appear on the chin or upper lip at the same time as their scalp hair becomes finer.",
                ],
                puces: [
                    "drier, thinner skin that creases more easily",
                    "loss of firmness along the jawline and neck",
                    "less dense, more brittle hair",
                    "thicker hairs on the face",
                    "vaginal dryness and intimate discomfort",
                    "more visible pigment spots",
                ],
            },
            {
                h2: "The consultation",
                paragraphes: [
                    "The interview reviews your symptoms, your personal and family history, your current treatments and what weighs on you most day to day. The clinical examination assesses skin quality, hair density and, if you wish, the condition of the mucous membranes.",
                    "A blood work-up may be suggested. The indications and contraindications of hormone replacement therapy are explained without shortcuts: this treatment is not suitable for everyone, and the decision is taken after assessing your personal situation.",
                ],
            },
            {
                h2: "What can be offered",
                paragraphes: [
                    "For the skin, treatments aim to support tissue quality: mesotherapy, skinboosters, microneedling, peels suited to mature skin, collagen stimulators and hyaluronic acid injections when laxity justifies them.",
                    "For the hair, the hair loss work-up points towards scalp mesotherapy and long-term treatment. For intimate comfort, dryness and atrophy are managed specifically, and can be raised without embarrassment: this is a frequent and treatable reason for consulting.",
                    "Every proposal is prioritised, explained, and — for aesthetic procedures — covered by a written quote with a cooling-off period.",
                ],
            },
        ],
        faq: [
            [
                "When should I consult?",
                "As soon as the changes bother you, without waiting for your periods to stop completely. Perimenopause is precisely the period when early care of the skin and hair is most worthwhile.",
            ],
            [
                "Is hormone replacement therapy compulsory?",
                "No. Hormone replacement therapy is one option, subject to precise indications and contraindications. Many women are followed and relieved without it.",
            ],
            [
                "Can vaginal dryness really be treated?",
                "Yes. It is one of the most common symptoms and one of the least often reported by patients, even though effective care exists. The consultation assesses the situation and offers a suitable solution.",
            ],
        ],
        related: ["sopk", "chute-de-cheveux", "melasma"],
    },

    "epilation-laser": {
        nav: "Laser hair removal",
        title: "Laser hair removal and electrolysis in Asnières-sur-Seine",
        description:
            "Laser hair removal of the face and body, electrolysis for light hair, management of hirsutism. Medical clinic in Asnières-sur-Seine, near Paris.",
        eyebrow: "Hair growth",
        h1: "Laser hair removal and electrolysis, by a doctor",
        lead: "At the Asnières-sur-Seine clinic, hair removal is not an isolated procedure: when hair growth is excessive, we also look for where it comes from. Laser and electrolysis are both performed on site, and often used together.",
        photo: { file: "salle.jpg", alt: "The treatment room equipped for laser hair removal" },
        condition: "Hirsutism",
        sections: [
            {
                h2: "Laser or electrolysis?",
                paragraphes: [
                    "Laser acts on the melanin in the hair: it is very effective on dark hair and makes it possible to treat large areas quickly, such as the legs, underarms or bikini line. It has no effect at all, however, on a hair without pigment.",
                    "Electrolysis destroys the follicle one hair at a time, whatever its colour. It is therefore indicated for white, blond, red or resistant hairs, and for finishing small areas such as the chin or the lip contour.",
                    "The two techniques do not compete: it is common to treat the bulk with laser, then finish with electrolysis on the hairs the laser cannot see. The choice is made after examining the area, the hair type and your skin phototype.",
                ],
            },
            {
                h2: "Why consult a doctor",
                paragraphes: [
                    "Hair growth that appears or worsens is not always a simple family trait. It can reveal an excess of androgens — PCOS, an androgen-related disorder, the effect of a treatment. In that case, removing the hair without looking for the cause means treating the sign while leaving the mechanism in place.",
                    "The doctor assesses the extent of hair growth, looks for associated signs (acne, irregular cycles, hair loss) and prescribes a hormonal work-up if necessary. Treating the cause then makes hair removal more lasting.",
                    "The consultation is also when contraindications are checked, settings are adapted to your skin and the framework is set: the likely number of sessions, the interval between them, and the precautions before and after.",
                ],
            },
            {
                h2: "How the sessions work",
                paragraphes: [
                    "Sessions are several weeks apart in order to follow the hair growth cycle: only follicles in the active phase respond to treatment, which is why a full cycle requires several sessions.",
                    "The area must be shaved the day before, not waxed or tweezed in the preceding weeks, and protected from the sun both before and after. Recovery is usually straightforward: transient redness is common in the hours that follow.",
                    "The number of sessions depends on the area, the density, the colour of the hair and the cause of the hair growth. It is estimated during the consultation and never promised in advance.",
                ],
            },
        ],
        faq: [
            [
                "Is laser hair removal permanent?",
                "We speak of long-lasting rather than permanent hair removal. Most treated hairs do not grow back, but an active hormonal background can reactivate follicles: spaced maintenance sessions are then needed.",
            ],
            [
                "Does it hurt?",
                "The sensation is often described as a warm elastic band snapping. It varies with the area and with each person's threshold. Settings and the device's cooling system are adjusted to keep it bearable.",
            ],
            [
                "Can darker skin be treated?",
                "Yes, provided the device and the settings are suited to the phototype. That is precisely the value of a prior medical assessment: it determines what can be done without risk to your skin.",
            ],
        ],
        related: ["sopk", "melasma", "chute-de-cheveux"],
    },

    melasma: {
        nav: "Melasma",
        title: "Melasma and pigment spots: doctor in Asnières",
        description:
            "Melasma, pregnancy mask and brown patches: depigmenting protocol, peels and sun protection. Medical clinic in Asnières-sur-Seine, near Paris.",
        eyebrow: "Pigment spots",
        h1: "Melasma: treating the patches without irritating the skin",
        lead: "Melasma is a stubborn hyperpigmentation, often triggered by hormones and maintained by the sun. Its management is medical and gradual, and relies as much on preventing recurrence as on treating the patches.",
        photo: { file: "peau.jpg", alt: "Close-up of skin texture, illustrating skin care" },
        condition: "Melasma",
        sections: [
            {
                h2: "Recognising melasma",
                paragraphes: [
                    "Melasma appears as brown patches with irregular, symmetrical borders on the forehead, cheekbones, upper lip or chin. It is common during pregnancy — then called the pregnancy mask — and on hormonal contraception.",
                    "It should not be confused with sun spots, post-inflammatory marks after acne, or another pigmented lesion that calls for a dermatological opinion. That distinction determines the whole treatment: it is the first thing the consultation checks.",
                ],
            },
            {
                h2: "The treatment protocol",
                paragraphes: [
                    "Treatment rests on three inseparable pillars: strict daily sun protection, a prescribed and monitored depigmenting treatment, and treatments carried out at the clinic — suitable peels, sometimes microneedling or mesotherapy depending on the skin type.",
                    "Overly aggressive procedures are counterproductive: on melasma, poorly controlled inflammation restarts pigmentation. Sessions are therefore spaced, gradual and reassessed at every stage.",
                    "It takes several months. This is said at the first consultation, because regularity matters more than the intensity of the sessions.",
                ],
            },
            {
                h2: "Preventing recurrence",
                paragraphes: [
                    "Melasma readily comes back as soon as sun protection stops or when a hormonal treatment is resumed. Maintenance is therefore part of the treatment: high-factor sunscreen all year round, including on cloudy days and behind glass, and physical protection in summer.",
                    "We review your contraception and your current treatments, because some promote pigmentation. This comprehensive approach is what distinguishes medical care from a simple beauty treatment.",
                ],
            },
        ],
        faq: [
            [
                "Is laser indicated for melasma?",
                "Laser is not the first-line treatment: badly chosen, it can make melasma worse by restarting pigmentation. Management begins with sun protection, depigmenting agents and suitable peels.",
            ],
            [
                "How long before I see a difference?",
                "The first changes are usually appreciated after several weeks of regular treatment. Assessment is based on photographs taken at the clinic under the same lighting conditions.",
            ],
            [
                "Can I be treated during pregnancy or breastfeeding?",
                "Some depigmenting treatments are contraindicated in these situations. The consultation makes it possible to adapt care and, often, to focus first on sun protection.",
            ],
        ],
        related: ["menopause", "sopk", "epilation-laser"],
    },

    "chute-de-cheveux": {
        nav: "Hair loss",
        title: "Hair loss: medical work-up in Asnières-sur-Seine",
        description:
            "Hair loss in women: assessment of the cause, scalp mesotherapy and long-term treatment. Medical clinic in Asnières-sur-Seine, near Paris.",
        eyebrow: "Hair",
        h1: "Hair loss: find the cause before treating",
        lead: "Hair loss in women almost always has an explanation: hormonal, nutritional, inflammatory or reactive. The work-up carried out at the Asnières-sur-Seine clinic aims to identify it before starting any treatment.",
        photo: { file: "mur.jpg", alt: "A wall of the clinic, warm and bright atmosphere" },
        condition: "Alopecia",
        sections: [
            {
                h2: "The different types of hair loss",
                paragraphes: [
                    "A first distinction is made between sudden, diffuse shedding that occurs a few months after a trigger — childbirth, a deficiency, a diet, an infection, emotional shock, stopping contraception — and gradual loss that settles in and changes the hairline.",
                    "In the second case, the parting widens on the top of the scalp while the temples stay preserved for a long time: this is the classic profile of androgen-related alopecia, common with PCOS or after the menopause.",
                ],
                puces: [
                    "a widening parting, scalp visible in the light",
                    "a noticeably thinner ponytail",
                    "finer, shorter hair that no longer grows as long",
                    "very heavy shedding when washing or brushing",
                    "itching or a sensitive scalp",
                ],
            },
            {
                h2: "The work-up",
                paragraphes: [
                    "The consultation goes back over the timeline of the hair loss, family history, treatments, diet and the events of recent months. The examination assesses density, the frontal hairline, the condition of the scalp and the quality of the hair shafts.",
                    "A blood work-up is usually prescribed: iron and ferritin, thyroid, vitamin D, and a hormonal work-up depending on the context. It is what makes it possible to tell a correctable deficiency from a hormonal mechanism, and to avoid unnecessary treatments.",
                ],
            },
            {
                h2: "The possible treatments",
                paragraphes: [
                    "Care first corrects what can be corrected: iron deficiency, thyroid dysfunction, hormonal imbalance. That is the foundation, and it is sometimes enough to stop reactive shedding.",
                    "Depending on the case, a local or systemic treatment prescribed by the doctor and sessions of scalp mesotherapy are added, delivering active ingredients that stimulate regrowth directly to the hair bulb.",
                    "The pace is explained from the outset: hair grows about one centimetre a month, so a hair treatment is assessed over several months, with photographs to support it.",
                ],
            },
        ],
        faq: [
            [
                "How long before I see a result?",
                "Allow several months: the first phase is about stopping the shedding, regrowth comes afterwards. Photographic follow-up at the clinic makes it possible to judge progress objectively.",
            ],
            [
                "Is scalp mesotherapy painful?",
                "The micro-injections are superficial and brief. The sensation is generally well tolerated, and the session lasts about twenty minutes.",
            ],
            [
                "Can hair loss be the first sign of PCOS?",
                "Yes, especially when it comes with acne, irregular cycles or excess hair growth. That is one of the reasons why the hair work-up includes a hormonal assessment.",
            ],
        ],
        related: ["sopk", "menopause", "epilation-laser"],
    },

    "acide-hyaluronique": {
        nav: "Hyaluronic acid",
        title: "Hyaluronic acid injections in Asnières-sur-Seine",
        description:
            "Hyaluronic acid injections and collagen stimulators in Asnières-sur-Seine: under-eye hollows, folds, jawline, lips. Procedures performed by a doctor.",
        eyebrow: "Volumes and wrinkles",
        h1: "Hyaluronic acid injections, in Asnières-sur-Seine",
        lead: "Restoring support where the face has hollowed, without transforming it. Injections are performed by a doctor, after a consultation, with a written quote and a cooling-off period.",
        photo: { file: "accueil.jpg", alt: "The reception of Aurassium Clinic in Asnières-sur-Seine" },
        condition: "Skin ageing",
        sections: [
            {
                h2: "What hyaluronic acid does, and does not do",
                paragraphes: [
                    "Hyaluronic acid is a molecule naturally present in the skin, where it retains water and supports the tissues. Injected, it fills a hollow, restores volume or hydrates deeply depending on its density.",
                    "It does not tighten the skin and does not replace surgery when laxity is significant. Nor does it stop ageing: its effect is temporary, from several months to more than a year depending on the area and the product.",
                    "Saying what a treatment will not do is part of the consultation. It is on that condition that the result matches what was announced.",
                ],
                puces: [
                    "hollow under-eye circles and tear troughs",
                    "nasolabial folds and marionette lines",
                    "cheekbones and temples",
                    "jawline and chin",
                    "lips: hydration or discreet redefinition",
                    "hands",
                ],
            },
            {
                h2: "Collagen stimulators: a different logic",
                paragraphes: [
                    "Collagen stimulators do not fill: they stimulate the skin's own collagen production. The result builds gradually over several weeks and acts on quality and firmness rather than on volume.",
                    "The two approaches are often combined: hyaluronic acid restores structure where it is missing, while stimulators improve the texture and resilience of the skin. The plan is drawn up during the consultation, area by area.",
                ],
            },
            {
                h2: "Procedure, recovery and safety",
                paragraphes: [
                    "The consultation always precedes the injection: history, current treatments, expectations, contraindications. The products used are medical devices, injected in a medical setting.",
                    "The session lasts fifteen to thirty minutes depending on the areas. A topical anaesthetic or a product containing a local anaesthetic is used. Transient bruising and swelling are possible: it is wise not to schedule an injection the day before an important event.",
                    "A review appointment is offered to assess the result once the swelling has settled, and to touch up if necessary.",
                ],
            },
        ],
        faq: [
            [
                "Will it show?",
                "The aim is for people to find you rested, not “done”. Quantities are adapted to your face and can be placed in stages: it is always possible to add, never easy to remove.",
            ],
            [
                "How long does the result last?",
                "Depending on the area, the product and your metabolism, the effect usually lasts from several months to more than a year. Maintenance is then offered, with no obligation.",
            ],
            [
                "Is it painful?",
                "Discomfort is limited thanks to topical anaesthesia and products containing a local anaesthetic. The most sensitive areas, such as the lips, are specifically prepared.",
            ],
        ],
        related: ["peau-et-rides", "melasma", "menopause"],
    },

    "peau-et-rides": {
        nav: "Skin and wrinkles",
        title: "Skin and wrinkle treatments in Asnières-sur-Seine",
        description:
            "Skin quality and wrinkles in Asnières-sur-Seine: peels, microneedling, skinboosters, mesotherapy. A tailored medical protocol by Dr Souhali Massilia.",
        eyebrow: "Skin quality",
        h1: "Treating the skin and wrinkles",
        lead: "Dull complexion, enlarged pores, fine lines, skin that creases: skin quality is worked on gradually, with protocols suited to your skin type and to the season.",
        photo: { file: "mur.jpg", alt: "The bright, warm atmosphere of the clinic in Asnières-sur-Seine" },
        condition: "Skin ageing",
        sections: [
            {
                h2: "The techniques used at the clinic",
                paragraphes: [
                    "A peel exfoliates the skin in a controlled way to restart its renewal: superficial for the complexion and fine lines, medium for more marked irregularities. Its depth is chosen according to your skin, never according to a standard formula.",
                    "Microneedling creates micro-channels that stimulate collagen production and improve texture, acne scars and enlarged pores.",
                    "Skinboosters and mesotherapy deliver very fluid hyaluronic acid, vitamins and antioxidants directly into the dermis: the skin is plumped from within, without changing the volumes.",
                ],
                puces: [
                    "dull complexion and lack of radiance",
                    "dehydration lines",
                    "enlarged pores and uneven texture",
                    "acne scars",
                    "skin that creases and loses firmness",
                    "preparing the skin before an event",
                ],
            },
            {
                h2: "Building a protocol, not stacking up sessions",
                paragraphes: [
                    "The consultation assesses your skin type, its sensitivity, your sun exposure, your routine and your current treatments. Some techniques are not advisable on pigmented skin or skin prone to melasma: the protocol takes this into account.",
                    "Sessions are spaced out and reassessed. Intensity is no guarantee of results: skin that is attacked defends itself, becomes inflamed and pigments. Regularity and sun protection achieve more than the power of an isolated procedure.",
                    "When laxity goes beyond what skin quality can correct, hyaluronic acid injections or collagen stimulators take over, as a complement.",
                ],
            },
            {
                h2: "After the session",
                paragraphes: [
                    "Recovery depends on the technique: a few hours of redness after microneedling or mesotherapy, a few days of peeling after a medium peel. Everything is explained beforehand, so that you can choose the right time.",
                    "High sun protection is essential afterwards, whatever the season: it is what protects the benefit obtained and prevents post-inflammatory marks.",
                ],
            },
        ],
        faq: [
            [
                "How many sessions are needed?",
                "Most skin quality protocols involve three to four sessions a few weeks apart, followed by maintenance. The exact number is estimated during the consultation, according to the starting point.",
            ],
            [
                "Can treatments be done in summer?",
                "Some techniques allow it with strict sun protection, others are postponed to the autumn. This is always discussed, especially for pigmented skin.",
            ],
            [
                "What is the difference between a skinbooster and injected hyaluronic acid?",
                "A skinbooster hydrates and improves skin quality without creating volume; filler hyaluronic acid restores a structure or a hollow. The two can be combined.",
            ],
        ],
        related: ["acide-hyaluronique", "melasma", "acne"],
    },

    "sante-intime": {
        nav: "Intimate health",
        title: "Vaginal dryness: treatment in Asnières-sur-Seine",
        description:
            "Vaginal dryness and atrophy in Asnières-sur-Seine: medical consultation, management of discomfort linked to the menopause. Dr Souhali Massilia.",
        eyebrow: "Intimate health",
        h1: "Vaginal dryness: talk about it, then treat it",
        lead: "Vaginal dryness affects one woman in two after the menopause, and far earlier for some. It is a frequent reason for consulting, it can be treated — and it deserves better than silence.",
        photo: { file: "cafe.jpg", alt: "The clinic's coffee corner, a discreet and welcoming waiting area" },
        condition: "Vulvovaginal atrophy",
        sections: [
            {
                h2: "Signs we often keep to ourselves",
                paragraphes: [
                    "Falling oestrogen levels thin the mucous membrane and reduce natural lubrication. Dryness, tightness, burning, daily discomfort and often pain during intercourse then set in.",
                    "These signs are neither inevitable nor an unavoidable consequence of age. They also appear after certain treatments, after childbirth, during breastfeeding or on contraception, and get worse without care.",
                ],
                puces: [
                    "daily dryness and tightness",
                    "burning or itching",
                    "pain during intercourse",
                    "recurrent urinary infections or thrush",
                    "a feeling of laxity or discomfort",
                ],
            },
            {
                h2: "The consultation",
                paragraphes: [
                    "The interview establishes how long the signs have been present, their impact and the hormonal context. An examination is offered, never imposed, and explained at every step.",
                    "Care is medical: local treatments, specific moisturisers, local hormonal treatment when it is indicated and not contraindicated, and treatments carried out at the clinic to improve the quality of the mucous membrane.",
                    "The options are presented with their limits and their contraindications, including where a medical history rules out certain treatments.",
                ],
            },
            {
                h2: "A subject treated like any other",
                paragraphes: [
                    "The consultation takes place in a medical setting, without judgement and without euphemism. Many patients wait years before mentioning it, believing that it cannot be treated.",
                    "Vaginal dryness is often part of a wider picture — menopause, drier skin, finer hair — which is managed as a whole at the clinic.",
                ],
            },
        ],
        faq: [
            [
                "Do I have to be menopausal to consult?",
                "No. Vaginal dryness can occur much earlier: after childbirth, during breastfeeding, on certain treatments or contraceptives, or with no obvious hormonal cause.",
            ],
            [
                "Are the treatments hormonal?",
                "Not necessarily. There are non-hormonal, local options, as well as treatments carried out at the clinic. The choice depends on your medical history and your preferences.",
            ],
            [
                "Can it improve lastingly?",
                "Yes, provided there is follow-up: the mucous membrane becomes fragile again if care is interrupted. Maintenance is therefore often planned.",
            ],
        ],
        related: ["menopause", "peau-et-rides", "sopk"],
    },

    acne: {
        nav: "Adult acne",
        title: "Adult female acne: doctor in Asnières-sur-Seine",
        description:
            "Adult acne, hormonal acne and scarring: medical assessment and treatment in Asnières-sur-Seine. Consultation with Dr Souhali Massilia (92600).",
        eyebrow: "Skin",
        h1: "Adult female acne: looking for the hormonal cause",
        lead: "Acne that appears or persists after 25 is not teenage acne that has overstayed. In adult women it is very often linked to hormones — and that is where care begins.",
        photo: { file: "bureau.jpg", alt: "Treatment room of the clinic in Asnières-sur-Seine" },
        condition: "Acne",
        sections: [
            {
                h2: "Recognising hormonal acne",
                paragraphes: [
                    "It tends to sit on the lower face: jawline, chin, neck. The lesions are often deep, inflammatory and painful, and flare in the second half of the cycle.",
                    "When it comes with irregular cycles, excess hair growth or hair loss, the possibility of excess androgens — in particular PCOS — must be explored. Treating the skin without exploring that background exposes you to relapse as soon as treatment stops.",
                ],
                puces: [
                    "lesions on the jawline, chin and neck",
                    "flare-ups timed with the cycle",
                    "deep, painful lesions",
                    "lasting marks and scars",
                    "association with excess hair growth or hair loss",
                ],
            },
            {
                h2: "Work-up and treatment",
                paragraphes: [
                    "The consultation reviews the history of the acne, treatments already tried, contraception and the skincare routine. A hormonal work-up is prescribed when the picture justifies it.",
                    "Treatment combines medical management of the acne itself and, where necessary, of the underlying hormonal imbalance. Unsuitable routines, which are common, are corrected: acne-prone skin that has been stripped becomes more reactive and marks more easily.",
                    "Once the inflammation is under control, marks and scars can be worked on at the clinic with microneedling or suitable peels — never on acne that is still active.",
                ],
            },
            {
                h2: "The marks left by acne",
                paragraphes: [
                    "A distinction must be made between post-inflammatory marks, brown or red, which fade with time and sun protection, and depressed scars, which require specific treatment.",
                    "Sun protection is decisive: on acne-prone skin, the sun darkens marks lastingly. It is the first piece of advice given in consultation, before any session.",
                ],
            },
        ],
        faq: [
            [
                "Should I stop my contraception?",
                "Not without medical advice. Some contraceptives make acne worse, others improve it: the decision is taken in consultation, taking your whole situation into account.",
            ],
            [
                "How long before I see an improvement?",
                "Acne treatment is assessed over several weeks to several months. The first weeks may even include a flare-up phase, which is announced in advance.",
            ],
            [
                "Can the scars be treated at the same time?",
                "No. Inflammation is brought under control first, then the marks are treated. Working on skin that is still active risks making pigmentation worse.",
            ],
        ],
        related: ["sopk", "peau-et-rides", "melasma"],
    },
};

export default soinsEn;
